import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { StandardPageWrapper } from "../../components/layout/StandardPageWrapper";
import { PageHeader } from "../../components/layout/header/Header";
import { useIntegrationsSettings } from "../../components/superorg/settings/IntegrationsSettings";
import {
  ZeroStateHero,
  ZeroStateVariant,
} from "../../components/organisms/zerostate/ZeroStateHero";
import { VendorIntegrationConfigurationContainer } from "../../components/organisms/vendor/VendorIntegrationConfigurationContainer";
import { useParagon } from "../../hooks/useParagon";
import Drawer, { DrawerRef } from "../../components/organisms/drawer/Drawer";
import { OrganizationGoogleAnalyticsForm } from "./OrganizationGoogleAnalyticsForm";
import { useOrganizationVendors, usePrimaryOrganization } from "../../hooks";
import { isGoogleAnalytics } from "../../util/vendors";
import { Api } from "../../../common/service/Api";
import _ from "lodash";
import { PrivacyCenterIntegrationVendorDto } from "../../../common/service/server/dto/PrivacyCenterIntegrationsDto";
import { ExplanatoryCard, Modal, SimpleCheckboxControlled, useSnackBar } from "@truevault/aquilo";
import { DEVELOPER_INSTRUCTIONS_SURVEY_NAME } from "../stay-compliant/PrivacyNoticeChecklistItems";
import { usePatchSurveyAnswer, useSurvey } from "../../../common/hooks/api/useSurvey";
import { DevInstructionsFinishedApiIntegrationsSetupSlug } from "../stay-compliant/WorkflowsDashboardPage";
import { useOnboardingProgress } from "../../../common/hooks/useOnboardingProgress";
import { useQueryClient } from "@tanstack/react-query";
import { ProgressFooter, ProgressFooterActions } from "../get-compliant/ProgressFooter";
import { Routes } from "../../root/routes";
import { useHistory, useLocation } from "react-router";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import { AutomationHistoryTab } from "./components/AutomationHistoryTab";
import { TabPanel } from "../../components";
import { useQueryTabs } from "../../hooks/useTabs";
import { TabOptions } from "../../components/Tabs";
import {
  useEditModeReadOnlyGuard,
  useIsEditModeReadOnly,
} from "../../edit-mode/useIsEditModeReadOnly";

const TAB_OPTIONS: TabOptions = [
  { label: "Connections", value: "connections" },
  { label: "Automation History", value: "automation_history" },
];

const INTERNAL_SETTINGS_SURVEY_NAME = "internal-settings";
const HAS_INVALID_INTEGRATION_CREDENTIAL_SLUG = "has-invalid-integration-credential";
// vendorAutomationName is the Paragon integration key (e.g. "googleAnalytics", "shopify"), not the
// vendor slug. Meta Ads is registered in Paragon as "facebookAds".
const META_ADS_AUTOMATION_NAMES = new Set(["facebookAds"]);

const isMetaAdsIntegration = (vendor: PrivacyCenterIntegrationVendorDto) => {
  const vendorName = vendor.vendorName.toLowerCase();

  return (
    META_ADS_AUTOMATION_NAMES.has(vendor.vendorAutomationName) ||
    vendorName === "meta ads" ||
    vendorName === "meta ad network"
  );
};

export const PrivacyCenterIntegrations = () => {
  const reactRouterHistory = useHistory();
  const routerLocation = useLocation();

  const [org, orgRequest, refreshOrg, invalidateOrg] = usePrimaryOrganization();
  const organizationId = org?.id;
  const { settings, privacyCenter, privacyCenterRequest, integrationsRequest, getLatestData } =
    useIntegrationsSettings();
  const [dataRecipients, dataRecipientsRequest] = useOrganizationVendors(organizationId);
  const {
    requests,
    isAuthorized,
    onConnectOrEditVendor,
    onReconnectVendor,
    onDisconnectVendor,
    connectShopify,
    isShopifyLoading,
  } = useParagon(settings);

  const [disconnectErrorVendor, setDisconnectErrorVendor] =
    useState<PrivacyCenterIntegrationVendorDto | null>(null);
  const [metaAdsConnectVendor, setMetaAdsConnectVendor] =
    useState<PrivacyCenterIntegrationVendorDto | null>(null);
  const [metaAdsConnectConfirmed, setMetaAdsConnectConfirmed] = useState(false);

  const enqueueSnackbar = useSnackBar();

  const [tab, tabs] = useQueryTabs(TAB_OPTIONS, "connections");
  const isEditModeReadOnly = useIsEditModeReadOnly();
  const guardReadOnlyMode = useEditModeReadOnlyGuard();

  const featureEnabled = settings?.featureEnabled && settings.integrationCountMax > 0;

  // Handle malformed URLs where ? is used instead of & for parameter separation
  const parseQuery = (search: string): Record<string, string> => {
    if (!search) return {};

    // Remove leading ?
    let queryString = search.startsWith("?") ? search.slice(1) : search;

    // Handle malformed URLs like "appIntegration=googleAnalytics?error=access_denied"
    // Split by ? first, then by & to handle all cases
    const parts = queryString.split(/[?&]/);
    const params: Record<string, string> = {};

    parts.forEach((part) => {
      if (part.includes("=")) {
        const [key, value] = part.split("=");
        params[decodeURIComponent(key)] = decodeURIComponent(value);
      }
    });

    return params;
  };

  const query = parseQuery(routerLocation.search);
  const {
    appIntegration,
    error: gaError,
    error_message: gaErrorMessage,
    error_description: gaErrorDescription,
    reconnect: reconnectVendor,
  } = query as {
    appIntegration?: string;
    error?: string;
    error_message?: string;
    error_description?: string;
    reconnect?: string;
  };

  const { answers: devInstructionsSurveyAnswers, setAnswer: setDevInstructionsSurveyAnswer } =
    useSurvey(organizationId, DEVELOPER_INSTRUCTIONS_SURVEY_NAME);

  const [onboardingProgress] = useOnboardingProgress(organizationId);
  const hasInvalidIntegrationCredential =
    onboardingProgress?.workflowsSetupOnboardingProgress?.hasInvalidIntegrationCredential ?? false;
  const patchInternalSettings = usePatchSurveyAnswer(organizationId, INTERNAL_SETTINGS_SURVEY_NAME);
  const queryClient = useQueryClient();

  // Whenever the loaded vendors show no invalid-credential integrations, clear the persistent flag so the navbar alert
  // dot disappears. The nightly heatlamp is the source of truth for setting it; this is the optimistic clear path.
  // Use a ref to fire once per "vendors-just-came-back-clean" transition; the mutation hook + queryClient identities
  // change on every render, so they can't safely live in the effect's dep array.
  const clearAttemptedRef = useRef(false);
  useEffect(() => {
    if (!settings?.vendors || settings.vendors.length === 0) {
      return;
    }
    const hasAnyInvalid = settings.vendors.some((v) => v.connectionError);
    if (hasAnyInvalid) {
      // Reset so a future clean transition can clear the flag again.
      clearAttemptedRef.current = false;
      return;
    }
    if (!hasInvalidIntegrationCredential || clearAttemptedRef.current) {
      return;
    }
    clearAttemptedRef.current = true;
    patchInternalSettings
      .mutateAsync([{ slug: HAS_INVALID_INTEGRATION_CREDENTIAL_SLUG, answer: "false" }])
      .then(() => queryClient.invalidateQueries(["orgOnboardingProgress", organizationId]))
      .catch((e) => {
        clearAttemptedRef.current = false;
        console.error(`Failed to clear invalid-integration-credential flag: ${e}`);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings?.vendors, hasInvalidIntegrationCredential]);

  const [seenIntegrationsWelcomeModal, setIntegrationsWelcomeModal] = useLocalStorage(
    "has_seen_integrations_welcome_modal",
    "false",
  );

  const didFinishApiIntegrationSetup =
    devInstructionsSurveyAnswers &&
    devInstructionsSurveyAnswers[DevInstructionsFinishedApiIntegrationsSetupSlug] === "true";

  // This is kind of a weird workaround for React Query's client-side caching. Sometimes an old GA ID would persist when
  // refreshing the page. But it was the client showing the old cached value from the org query; the actual ID was saved
  // correctly. We're going to try always invalidating/refreshing on load to see if we can bust the cache.
  useEffect(() => {
    if (refreshOrg && invalidateOrg) {
      const clearOrgCache = async () => {
        await invalidateOrg();
        void refreshOrg();
      };

      void clearOrgCache();
    }
  }, [refreshOrg, invalidateOrg]);
  // ---

  //@region Integration sidebar drawer
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
  const drawerRef = useRef<DrawerRef>(null);
  const [drawerContent, setDrawerContent] = useState<React.ReactNode>(null);
  //#endregion

  //#region Google Analytics APIs
  const googleAnalytics = useMemo(
    () => dataRecipients?.find((dr) => isGoogleAnalytics(dr)),
    [dataRecipients],
  );

  const saveGAOrganizationData = useCallback(
    (gaOrganizationData) =>
      Api.organization.updateGaWebProperty(organizationId, gaOrganizationData),
    [organizationId],
  );
  const saveGAPCData = useCallback(
    (gaPCData) => {
      Api.privacyCenter.updateGaMeasurementId(organizationId, privacyCenter?.id, gaPCData);
    },
    [organizationId, privacyCenter],
  );

  const debouncedSaveOrgGASettings = _.debounce(saveGAOrganizationData, 300);
  const debouncedSavePCGASettings = _.debounce(saveGAPCData, 300);
  //#endregion

  /**
   * Open the connection sidebar (drawer) for the specified vendor.
   * @param integrationName The paragon integration name of the vendor for which to open the custom connection drawer.
   */
  const openCustomConnectionSidebar = useCallback(
    (integrationName: string) => {
      if (guardReadOnlyMode()) {
        return;
      }

      if (isDrawerOpen) {
        return;
      }

      switch (integrationName) {
        case "googleAnalytics":
          setDrawerContent(
            <>
              <OrganizationGoogleAnalyticsForm
                organization={org}
                privacyCenter={privacyCenter}
                googleAnalytics={googleAnalytics}
                showDoItLater={false}
                invalidateOrg={invalidateOrg}
                onUpdateGAPropertyId={debouncedSaveOrgGASettings}
                onUpdateGAMeasurementId={debouncedSavePCGASettings}
                onErrorStateUpdate={(_) => {}}
              />
            </>,
          );
          break;
        default:
      }
      setIsDrawerOpen(true);
    },
    [
      debouncedSaveOrgGASettings,
      debouncedSavePCGASettings,
      googleAnalytics,
      guardReadOnlyMode,
      invalidateOrg,
      isDrawerOpen,
      org,
      privacyCenter,
    ],
  );

  const closeCustomConnectionSidebar = () => {
    getLatestData(); // Need to fetch latest data in case e.g., GA got unauthorized
    setIsDrawerOpen(false);
  };

  const connectVendor = useCallback(
    async (vendor: PrivacyCenterIntegrationVendorDto) => {
      switch (vendor.vendorAutomationName) {
        case "googleAnalytics":
          openCustomConnectionSidebar(vendor.vendorAutomationName);
          break;
        case "shopify":
          // TODO: Display an instructions modal here instead of forwarding the user to Shopify admin (POL-4655).
          window.location.href = "https://apps.shopify.com/truevault";
          break;
        default:
          await onConnectOrEditVendor(vendor);
          getLatestData();
      }
    },
    [getLatestData, onConnectOrEditVendor, openCustomConnectionSidebar],
  );

  const closeMetaAdsConnectModal = () => {
    setMetaAdsConnectVendor(null);
    setMetaAdsConnectConfirmed(false);
  };

  const confirmMetaAdsConnect = () => {
    if (!metaAdsConnectVendor || guardReadOnlyMode()) {
      return;
    }

    const vendor = metaAdsConnectVendor;
    closeMetaAdsConnectModal();
    window.setTimeout(() => {
      connectVendor(vendor).catch((e) =>
        console.error(`Error connecting ${vendor.vendorAutomationName}: ${e}`),
      );
    }, 0);
  };

  //#region On-load effects
  useEffect(() => {
    if (isEditModeReadOnly) {
      return;
    }

    if (isAuthorized && settings?.featureEnabled) {
      connectShopify(getLatestData).catch((e) => {
        console.error(`Error connecting Shopify via Paragon: ${e}`);
      });
    }
  }, [connectShopify, getLatestData, isAuthorized, isEditModeReadOnly, settings]);

  useEffect(() => {
    // If coming from a redirect for e.g. GA OAuth, any special handling like opening the drawer.
    if (appIntegration === "googleAnalytics") {
      if (gaError || gaErrorMessage || gaErrorDescription) {
        // Handle OAuth errors - support both backend format (error_message) and Google OAuth standard (error_description)
        const errorMsg =
          gaErrorMessage ||
          gaErrorDescription ||
          gaError ||
          "An error occurred during Google Analytics authorization";
        enqueueSnackbar(`Failed to connect Google Analytics: ${errorMsg}`, { variant: "error" });
      } else {
        // Handle success case
        enqueueSnackbar("Connected Google Analytics", { variant: "success" });
      }
      // Consume query string now that we've used it (so we don't keep re-toasting when deps change).
      history.replaceState(null, "", window.location.href.split("?")[0]);
    }
  }, [appIntegration, gaError, gaErrorMessage, gaErrorDescription, enqueueSnackbar]);

  // Handle reconnect parameter - auto-open connection dialog for the specified vendor
  useEffect(() => {
    if (isEditModeReadOnly || !reconnectVendor || !isAuthorized || !settings?.vendors) {
      return;
    }

    // Find the vendor that matches the reconnect parameter
    const vendor = settings.vendors.find((v) => v.vendorAutomationName === reconnectVendor);
    if (!vendor) {
      return;
    }

    // Clear the reconnect parameter from URL using React Router (triggers re-render with updated query)
    const newSearch = new URLSearchParams(routerLocation.search);
    newSearch.delete("reconnect");
    const newSearchString = newSearch.toString();
    reactRouterHistory.replace({
      search: newSearchString ? `?${newSearchString}` : "",
    });

    // Open the appropriate connection dialog based on vendor type
    switch (vendor.vendorAutomationName) {
      case "googleAnalytics":
        openCustomConnectionSidebar(vendor.vendorAutomationName);
        break;
      case "shopify":
        // Shopify uses OAuth, so we just redirect (don't auto-trigger this)
        break;
      default:
        // Use Paragon for other vendors
        onConnectOrEditVendor(vendor)
          .then(() => getLatestData())
          .catch((e) => console.error(`Error opening reconnect dialog: ${e}`));
    }
  }, [
    reconnectVendor,
    isAuthorized,
    settings?.vendors,
    openCustomConnectionSidebar,
    onConnectOrEditVendor,
    getLatestData,
    isEditModeReadOnly,
    reactRouterHistory,
    routerLocation.search,
  ]);
  //#endreigon

  /**
   * We show the vendor list under a few conditions, and some of them need to filter (e.g., to just Google Analytics for
   * orgs that aren't licensed).
   */
  const getVendorList = useCallback(
    (filter: (vendor: PrivacyCenterIntegrationVendorDto) => boolean) => {
      return (
        <VendorIntegrationConfigurationContainer
          vendors={settings?.vendors?.filter(filter) ?? []}
          onConnectClick={async (vendor) => {
            if (guardReadOnlyMode()) {
              return;
            }

            if (isMetaAdsIntegration(vendor)) {
              setMetaAdsConnectVendor(vendor);
              setMetaAdsConnectConfirmed(false);
              return;
            }

            await connectVendor(vendor);
          }}
          onEditClick={async (vendor) => {
            if (guardReadOnlyMode()) {
              return;
            }

            switch (vendor.vendorAutomationName) {
              case "googleAnalytics":
                openCustomConnectionSidebar(vendor.vendorAutomationName);
                break;
              case "shopify": // Default behavior for Shopify
              default:
                await onConnectOrEditVendor(vendor);
                getLatestData();
            }
          }}
          onReconnectClick={async (vendor) => {
            if (guardReadOnlyMode()) {
              return;
            }

            switch (vendor.vendorAutomationName) {
              case "googleAnalytics":
                openCustomConnectionSidebar(vendor.vendorAutomationName);
                break;
              case "shopify":
                window.location.href = "https://apps.shopify.com/truevault";
                break;
              default:
                await onReconnectVendor(vendor);
                getLatestData();
            }
          }}
          onDisconnectClick={async (vendor) => {
            if (guardReadOnlyMode()) {
              return;
            }

            // POL-7347: Disconnect button is rendered only for vendors with connectionError === true (Paragon
            // credentialStatus INVALID). googleAnalytics is not Paragon-managed so it can't reach that state.
            if (vendor.vendorAutomationName === "googleAnalytics") {
              return;
            }
            try {
              await onDisconnectVendor(vendor);
              getLatestData();
              enqueueSnackbar(`Disconnected ${vendor.vendorName}`, { variant: "success" });
            } catch (e) {
              console.error(`Error disconnecting ${vendor.vendorAutomationName}: ${e}`);
              setDisconnectErrorVendor(vendor);
            }
          }}
          loading={isShopifyLoading}
        />
      );
    },
    [
      connectVendor,
      enqueueSnackbar,
      getLatestData,
      guardReadOnlyMode,
      isShopifyLoading,
      onConnectOrEditVendor,
      onDisconnectVendor,
      onReconnectVendor,
      openCustomConnectionSidebar,
      settings?.vendors,
    ],
  );

  // Future: if it winds that the query out to Paragon takes a long-ish time to get the active integrations for the org,
  //         we can split the `integrationsRequest` out and display a friendly loading message in the meantime...
  return (
    <StandardPageWrapper
      requests={[
        ...requests,
        orgRequest,
        dataRecipientsRequest,
        privacyCenterRequest,
        integrationsRequest,
      ]}
      footer={
        !didFinishApiIntegrationSetup && (
          <ProgressFooter
            actions={
              <ProgressFooterActions
                nextContent="Done with Integrations"
                nextOnClick={async () => {
                  await setDevInstructionsSurveyAnswer(
                    DevInstructionsFinishedApiIntegrationsSetupSlug,
                    "true",
                  );
                  reactRouterHistory.push(Routes.workflows.dashboard);
                }}
              />
            }
          />
        )
      }
    >
      <div className="max-width-896">
        <PageHeader titleContent="API Integrations" />

        {seenIntegrationsWelcomeModal === "false" && (
          <WelcomeModal setSeenWelcomeModal={setIntegrationsWelcomeModal} />
        )}

        <div className="mb-md">{tabs}</div>

        <TabPanel tab={tab} value="connections">
          <div className={"privacy-center-settings privacy-center"}>
            {!featureEnabled && (
              <>
                {settings?.vendors?.length > 0 && (
                  <>
                    {getVendorList((vendor) => vendor.vendorAutomationName === "googleAnalytics")}
                    <hr />
                  </>
                )}
                <ZeroStateHero
                  variant={ZeroStateVariant.DEFAULT}
                  title={settings?.vendors?.length > 0 ? "Ready for more?" : "Let's get started!"}
                  text="We're constantly expanding our integrations. Get in touch to experience the convenience of automation."
                  imageURL="/assets/images/surveys/2023q4/survey-review-hero.svg"
                  ctaButtonText="Schedule a call"
                  ctaButtonOnClick={() => {
                    document.location =
                      "https://calendly.com/truevault-customer-success/support?hide_gdpr_banner=1&primary_color=5331C6&background_color=FFFFFF&text_color=1A1726";
                  }}
                />
              </>
            )}
            {featureEnabled && !isAuthorized && (
              <ZeroStateHero
                variant={ZeroStateVariant.LOADING}
                text="Please wait while we load your integrations."
              />
            )}
            {featureEnabled && isAuthorized && (
              <>
                {(!settings?.vendors || settings?.vendors?.length < 1) && (
                  <ZeroStateHero
                    title="No Vendors Available"
                    text="It looks like none of your data recipients currently support automation. Check back often as we build out new integrations."
                    imageURL="/assets/images/surveys/onboarding/data-minimization-hero.svg"
                  ></ZeroStateHero>
                )}
                {settings?.vendors?.length > 0 && (
                  <>
                    <div className="tw-boundary">
                      <ExplanatoryCard
                        topBody={
                          <div>
                            <h3 className="text-lg font-medium leading-6 text-neutral-800 mb-3">
                              Setting Up Integrations
                            </h3>
                            <div className="text-sm font-normal leading-5 text-gray-500 space-y-4">
                              <p>
                                Click "connect" to integrate your TrueVault account with the systems
                                displayed below.
                              </p>
                              <p>
                                Once you connect a system, we can automate data deletion when a
                                customer sends you a privacy request. This avoids the need for
                                manual deletion and saves you time.
                              </p>
                              <p>
                                Read more about{" "}
                                <a
                                  href="https://knowledge.truevault.com/article/311-automation-getting-started"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="font-medium text-sm leading-5 underline text-primary-600"
                                >
                                  Getting Started with Automation
                                </a>
                                . You can also check out our{" "}
                                <a
                                  href="https://knowledge.truevault.com/article/315-automation-api-key-setup"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="font-medium text-sm leading-5 underline text-primary-600"
                                >
                                  Guide on Setting Up Integrations
                                </a>
                                , which includes vendor-specific video guides.
                              </p>
                            </div>
                          </div>
                        }
                        expandable={false}
                      />
                    </div>
                    {getVendorList((_) => true)}
                  </>
                )}
              </>
            )}
          </div>
        </TabPanel>

        <TabPanel tab={tab} value="automation_history">
          <AutomationHistoryTab />
        </TabPanel>
      </div>
      <Drawer
        ref={drawerRef}
        isOpen={isDrawerOpen}
        onClose={closeCustomConnectionSidebar}
        content={drawerContent}
      />
      {disconnectErrorVendor && (
        <DisconnectErrorModal
          vendor={disconnectErrorVendor}
          onClose={() => setDisconnectErrorVendor(null)}
        />
      )}
      {metaAdsConnectVendor && (
        <MetaAdsConnectModal
          confirmed={metaAdsConnectConfirmed}
          onConfirmedChange={setMetaAdsConnectConfirmed}
          onCancel={closeMetaAdsConnectModal}
          onConnect={confirmMetaAdsConnect}
        />
      )}
    </StandardPageWrapper>
  );
};

function MetaAdsConnectModal({
  confirmed,
  onConfirmedChange,
  onCancel,
  onConnect,
}: {
  confirmed: boolean;
  onConfirmedChange: (value: boolean) => void;
  onCancel: () => void;
  onConnect: () => void;
}) {
  return (
    <div className="tw-boundary">
      <Modal
        title="Confirm Meta Ads connection"
        alignText="left"
        alignButton="right"
        firstButton="Cancel"
        firstButtonColor="tertiary"
        secondButton="Connect"
        secondButtonColor="primary"
        secondButtonDisabled={!confirmed}
        onFirstButtonClick={onCancel}
        onSecondButtonClick={onConnect}
        onClose={onCancel}
        showOverlay
        closeButton
      >
        <div className="text-sm text-neutral-700 space-y-4">
          <p>You only need to connect Meta if you are uploading lists directly into Meta Ads.</p>
          <p>
            If you manage Meta Ads lists through another vendor, like Klaviyo, you do not need to
            make this connection.
          </p>
          <SimpleCheckboxControlled
            name="meta-ads-direct-upload-confirmation"
            checked={confirmed}
            onChange={onConfirmedChange}
            label="Yes, I'm uploading directly into Meta (or I want to automate Meta anyway)"
          />
        </div>
      </Modal>
    </div>
  );
}

function DisconnectErrorModal({
  vendor,
  onClose,
}: {
  vendor: PrivacyCenterIntegrationVendorDto;
  onClose: () => void;
}) {
  return (
    <div className="tw-boundary">
      <Modal
        title={`We couldn't disconnect ${vendor.vendorName}`}
        alignText="left"
        firstButton="OK"
        firstButtonColor="primary"
        onFirstButtonClick={onClose}
        onClose={onClose}
        showOverlay
        closeButton
      >
        <div className="text-sm text-neutral-700 space-y-3">
          <p>
            {vendor.vendorName}&apos;s credentials are in an invalid state, and we were unable to
            remove the integration directly from this view.
          </p>
          <p>
            Please click <strong>Reconnect</strong> to re-authorize {vendor.vendorName}, then click{" "}
            <strong>Disconnect</strong> on the reconnected integration to remove it.
          </p>
          <p>
            If you are unable to reconnect to this integration, contact us at{" "}
            <a
              href="mailto:support@truevault.com"
              className="font-medium underline text-primary-600"
            >
              support@truevault.com
            </a>{" "}
            and we&apos;ll help you remove the integration.
          </p>
        </div>
      </Modal>
    </div>
  );
}

function WelcomeModal({ setSeenWelcomeModal }: { setSeenWelcomeModal: (value: string) => void }) {
  return (
    <div className="tw-boundary">
      <Modal
        title="Let's connect your vendors to TrueVault so you can process requests automatically"
        alignText="center"
        firstButton="Let's start"
        firstButtonColor="primary"
        onFirstButtonClick={() => {
          setSeenWelcomeModal("true");
        }}
        widthButton="full"
        showOverlay
        closeButton={false}
        onClose={() => {
          setSeenWelcomeModal("true");
        }}
      >
        <img
          className="w-[345px] h-[239px] mt-[20px] mx-auto"
          src="/assets/images/stay_compliant/api_integrations_welcome_modal_illustration.svg"
          alt="Integrations"
        />
      </Modal>
    </div>
  );
}
